import { useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Modal, Textarea } from '@/backoffice/components/ui';
import { DriverMissionCard } from '@/backoffice/components/driver/DriverMissionCard';
import { DriverGpsTracker } from '@/backoffice/components/driver/DriverGpsTracker';
import { useDriverMissions, type MissionStatusFilter } from '@/backoffice/hooks/useDriverMissions';
import type { DriverMission } from '@/domains/driver/services/driver.service';
import { Package, Loader2 } from 'lucide-react';

const statusFilters: { value: MissionStatusFilter; label: string }[] = [
  { value: 'all', label: 'Toutes' },
  { value: 'available', label: 'Disponibles' },
  { value: 'pending', label: 'En attente' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'delivered', label: 'Livrées' },
  { value: 'rejected', label: 'Refusées' },
];

export function ProMissionsPage() {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedMission, setSelectedMission] = useState<DriverMission | null>(null);

  const {
    missions,
    loading,
    actionLoading,
    error,
    activeFilter,
    setActiveFilter,
    handleAccept,
    handleReject,
    handleComplete,
  } = useDriverMissions();

  const onRejectClick = (mission: DriverMission) => {
    setSelectedMission(mission);
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!selectedMission) return;
    await handleReject(selectedMission.id, selectedMission.isAvailable);
    setShowRejectModal(false);
    setSelectedMission(null);
  };

  return (
    <DashboardLayout userType="pro_driver" title="Missions">
      <DriverGpsTracker />
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div className="flex items-center gap-2 mb-6 overflow-x-auto">
        {statusFilters.map((filter) => (
          <Button
            key={filter.value}
            variant={activeFilter === filter.value ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter(filter.value)}
            className="whitespace-nowrap"
          >
            {filter.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement des missions...
        </div>
      ) : missions.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune mission</h3>
            <p className="text-gray-500">Vous n'avez pas de mission dans cette catégorie.</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
          {missions.map((mission) => (
            <DriverMissionCard
              key={mission.id}
              mission={mission}
              compact
              actionLoading={actionLoading}
              onAccept={() => handleAccept(mission.id, mission.isAvailable)}
              onReject={() => onRejectClick(mission)}
              onComplete={() => handleComplete(mission.id)}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Refuser la mission"
      >
        {selectedMission && (
          <div>
            <p className="text-gray-600 mb-4">
              Veuillez confirmer le refus de la mission {selectedMission.code}.
            </p>
            <Textarea placeholder="Raison du refus (optionnel)" className="mb-4" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>
                Annuler
              </Button>
              <Button variant="danger" onClick={confirmReject} isLoading={actionLoading}>
                Confirmer le refus
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
